import {
  CellPosition,
  CellsReferences,
  Independency,
  ReferenceType,
  Sheet,
} from '../../types';
import { SheetManagementSet } from '../../types/sheet/draft';
import { parseSingleAddress } from '../address/singleAddress';
import { createSheetDraft } from '../sheet/updates';
import { isCellPosition } from '../address/parsingAddress';
import { QueueItemType, QueueSet } from './queue';
import { areEqualPositions, convertPositionToArea } from '../area/area';
import { CellErrorsMessages } from '../address/formulaValidation';
import { Draft } from 'immer';
import {
  addAddressToRangeMatrix,
  convertParsedAddressToArea,
  isCellInsideRangeAddress,
  removeAddressFromRangeMatrix,
} from '../address/address';
import { createCell, deleteCell, getCell } from '../cells/cellCRUD';
import _ from 'lodash';

// #region clear independencies
export async function removeIndependencies(
  set: SheetManagementSet,
  depSheetId: string,
  depCellAddress: string,
  indepSheetId: string,
  indepAddress: string,
  addressType: ReferenceType = ReferenceType.CellFormula
) {
  const indepSheet = await createSheetDraft(indepSheetId, set);

  if (indepAddress.includes(':'))
    removeAddressRangeFromIndeps(
      depSheetId,
      depCellAddress,
      indepSheet,
      indepAddress,
      addressType
    );
  else
    removeCellFromIndeps(
      depSheetId,
      depCellAddress,
      indepSheet,
      indepAddress,
      addressType
    );
}

function removeCellFromIndeps(
  depSheetId: string,
  depCellAddress: string,
  indepSheet: Sheet,
  indepAddress: string,
  addressType: ReferenceType
) {
  const isCell = isCellPosition(indepAddress);
  if (!isCell) return;
  const position = isCell.position;
  const cellsReferences = indepSheet.cellsReferences;

  const cellRef = getCell(cellsReferences, position);
  const dependentList = cellRef?.i;
  if (dependentList) {
    const updatedDependentList = removeAddressFromDependentList(
      dependentList,
      depSheetId,
      depCellAddress,
      addressType
    );
    if (updatedDependentList.length) {
      cellRef.i = updatedDependentList;
    } else {
      delete cellRef.i;
      if (_.isEmpty(cellRef)) {
        deleteCell(cellsReferences, position);
      }
    }
  }
}

function removeAddressRangeFromIndeps(
  depSheetId: string,
  depCellAddress: string,
  independentSheet: Sheet,
  indepAddress: string,
  addressType: ReferenceType
) {
  const { rangeIndepsMatrix, rangeIndeps, maxColIndex, maxRowIndex } =
    independentSheet;
  const dependentList = rangeIndeps[indepAddress];

  if (dependentList) {
    const updatedDependentList = removeAddressFromDependentList(
      dependentList,
      depSheetId,
      depCellAddress,
      addressType
    );
    if (updatedDependentList.length) {
      rangeIndeps[indepAddress] = updatedDependentList;
    } else {
      delete rangeIndeps[indepAddress];
      removeAddressFromRangeIndepMatrix(
        rangeIndepsMatrix,
        indepAddress,
        maxColIndex,
        maxRowIndex
      );
    }
  }
}

const removeAddressFromDependentList = (
  dependentList: Independency[],
  depSheetId: string,
  depCellAddress: string,
  addressType: ReferenceType
) =>
  dependentList.filter(
    ({ sid, addr, type }) =>
      depSheetId !== sid && addr !== depCellAddress && type === addressType
  );

function removeAddressFromRangeIndepMatrix(
  rangeIndepsMatrix: string[][],
  indepAddress: string,
  maxColIndex: number,
  maxRowIndex: number
) {
  const area = convertParsedAddressToArea(
    parseSingleAddress(indepAddress),
    maxColIndex,
    maxRowIndex
  );

  removeAddressFromRangeMatrix(rangeIndepsMatrix, area, indepAddress);
}
// #endregion

// #region add Independencies
export async function addIndependencies(
  set: SheetManagementSet,
  depSheetId: string,
  depCellAddress: string,
  indepSheetId: string,
  indepAddress: string,
  addressType: ReferenceType = ReferenceType.CellFormula
) {
  const sheetsIdToName = set.staticEntityIdToNameMap.sheetsIdToName;

  if (!sheetsIdToName[indepSheetId]) return;

  const sheet = await createSheetDraft(indepSheetId, set);

  if (indepAddress.includes(':'))
    assignAddressToRangeIndeps(
      depSheetId,
      depCellAddress,
      sheet,
      indepAddress,
      addressType
    );
  else
    assignPosToCellIndeps(
      depSheetId,
      depCellAddress,
      sheet,
      indepAddress,
      addressType
    );
}

function assignPosToCellIndeps(
  depSheetId: string,
  depCellAddress: string,
  indepSheet: Draft<Sheet>,
  indepAddress: string,
  addressType: ReferenceType
) {
  const isCell = isCellPosition(indepAddress);
  if (!isCell) return;
  const position = isCell.position;
  const cellRef = createCell(indepSheet.cellsReferences, position);
  if (!cellRef?.i) {
    cellRef.i = [];
  }

  cellRef.i.push({
    sid: depSheetId,
    addr: depCellAddress,
    type: addressType,
  });
}

function assignAddressToRangeIndeps(
  depSheetId: string,
  depCellAddress: string,
  indepSheet: Draft<Sheet>,
  indepAddress: string,
  addressType: ReferenceType
) {
  const { rangeIndeps, rangeIndepsMatrix, maxColIndex, maxRowIndex } =
    indepSheet;

  let dependentList = rangeIndeps[indepAddress];
  if (!dependentList) {
    dependentList = rangeIndeps[indepAddress] = [];
    saveAddressInRangeIndepMatrix(
      rangeIndepsMatrix,
      indepAddress,
      maxColIndex,
      maxRowIndex
    );
  }

  dependentList.push({
    sid: depSheetId,
    addr: depCellAddress,
    type: addressType,
  });
}

function saveAddressInRangeIndepMatrix(
  rangeIndepsMatrix: string[][],
  indepAddress: string,
  maxColIndex: number,
  maxRowIndex: number
) {
  const area = convertParsedAddressToArea(
    parseSingleAddress(indepAddress),
    maxColIndex,
    maxRowIndex
  );
  addAddressToRangeMatrix(rangeIndepsMatrix, area, indepAddress);
  // for (let c = area.start.ci; c <= area.end.ci; c++) {
  //   if (!rangeIndepsMatrix[c]) rangeIndepsMatrix[c] = [];

  //   rangeIndepsMatrix[c].push(indepAddress);
  // }
}
// #endregion

// #region Queue Independencies
export async function queueIndependencies(
  set: SheetManagementSet,
  queueSet: QueueSet,
  masterCellPosition: CellPosition, // Initial Cell Whose Value Got Changed
  depSheetId: string,
  indepPosition: CellPosition
) {
  const sheetsIdToName = set.staticEntityIdToNameMap.sheetsIdToName;
  if (!sheetsIdToName[depSheetId]) return;

  const sheet = await createSheetDraft(depSheetId, set);

  const indeps = getCell(sheet.cellsReferences, indepPosition)?.i;
  if (indeps) {
    await handleIndependenciesQueue(queueSet, masterCellPosition, indeps);
  }

  await handleRangeIndependenciesQueue(
    queueSet,
    masterCellPosition,
    sheet,
    indepPosition
  );
}

async function handleRangeIndependenciesQueue(
  queueSet: QueueSet,
  masterCellPosition: CellPosition,
  indepSheet: Draft<Sheet>,
  indepPosition: CellPosition
) {
  const { rangeIndeps, rangeIndepsMatrix, maxColIndex, maxRowIndex } =
    indepSheet;

  const rangeColumnMatrix = rangeIndepsMatrix[indepPosition.ci];
  if (!rangeColumnMatrix) return;

  const cellArea = convertPositionToArea(indepPosition);
  for (const indepAddress of rangeColumnMatrix) {
    const isInside = isCellInsideRangeAddress(
      indepAddress,
      cellArea,
      maxColIndex,
      maxRowIndex
    );

    if (isInside) {
      const indeps = rangeIndeps[indepAddress];

      if (indeps) {
        await handleIndependenciesQueue(queueSet, masterCellPosition, indeps);
      }
    }
  }
}

async function handleIndependenciesQueue(
  queueSet: QueueSet,
  masterCellPosition: CellPosition,
  indeps: Independency[]
) {
  for (const { sid, addr, type } of indeps) {
    await putIndependenciesInQueue(
      queueSet,
      masterCellPosition,
      sid,
      addr,
      type
    );
  }
}

async function putIndependenciesInQueue(
  queueSet: QueueSet,
  masterCellPosition: CellPosition,
  sheetId: string,
  address: string, // For DV CF DF it will be id
  type: ReferenceType
) {
  switch (type) {
    case ReferenceType.CellFormula:
      await addToWaiting(queueSet, masterCellPosition, sheetId, address);
      break;
    case ReferenceType.ConditionalFormat:
    case ReferenceType.DataValidation:
    case ReferenceType.DynamicFormula:
    // TODO: Generate Formula
  }
}

async function addToWaiting(
  queueSet: QueueSet,
  masterCellPosition: CellPosition,
  sheetId: string,
  address: string
) {
  const isCell = isCellPosition(address);
  if (!isCell) return;
  const position = isCell.position;

  const { visited, waiting, queue } = queueSet;

  const areEqual = areEqualPositions(position, masterCellPosition);

  if (areEqual) {
    if (!queueSet.circularError) {
      queue.enqueue({
        type: QueueItemType.Error,
        circularError: true,
      });
      queueSet.circularError = CellErrorsMessages.CircularDep;
    }
  } else if (
    !visited.get(sheetId)?.has(address) &&
    !waiting.get(sheetId)?.has(address)
  ) {
    if (!waiting.has(sheetId)) waiting.set(sheetId, new Set());

    waiting.get(sheetId)?.add(address);

    queue.enqueue({
      type: QueueItemType.Cell,
      sheetId,
      address,
      position,
    });
  }
}
// #endregion
